import styles from './styles.module.scss';
import { Image, ToolTip } from '@starsoft/common/components';
import { TransactionResultProps } from '../props';
import { DepositMethodId, CommonModalsKey } from '@starsoft/common/enums';
import { useTranslation } from '../../../../hooks';
import { useCurrentModal } from '../../../../hooks/modals/useCurrentModal';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import TransactionResultDetailsSkeleton from './Skeleton';
import { TransactionsMapping } from '../../Common/Mapping';
import { DepositStatusId } from '@starsoft/common/models/deposit/status/id';

/**
 * TransactionResultDetails Component
 * Renders transaction details including ID, creation date, payment method, status and related URLs.
 * Can be rendered in minimal mode showing only essential information.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Transaction} props.transaction - Transaction object containing details to display
 * @param {boolean} props.minimal - If true, shows minimal information
 * @param {boolean} props.isLoading - Loading state flag
 * @returns {JSX.Element} A container with transaction details cards
 *
 * @example
 * const transaction = {
 *   id: "123",
 *   method: { id: DepositMethodId.Crypto, name: "Bitcoin" },
 *   status: { id: DepositStatusId.Paid },
 *   createdAt: "2024-01-01T00:00:00Z"
 * };
 *
 * <TransactionResultDetails
 *   transaction={transaction}
 *   minimal={false}
 *   isLoading={false}
 * />
 */
export default function TransactionResultDetails({
  transaction,
  minimal,
  isLoading,
}: TransactionResultProps) {
  const { t } = useTranslation('deposit');
  const modal: string | undefined = useCurrentModal(CommonModalsKey);

  /**
   * Gets the display name for the deposit method
   * For crypto deposits, returns the coin code
   * For other methods, returns the method name
   *
   * @returns {string} Display name for the deposit method
   *
   * @example
   * // For crypto deposit with BTC
   * getDepositMethodName() // Returns "BTC"
   *
   * // For PIX deposit
   * getDepositMethodName() // Returns "PIX"
   */
  function getDepositMethodName(): string {
    switch (transaction?.method?.id?.toString()) {
      case DepositMethodId.Crypto?.toString():
        return transaction?.coin?.code;
    }

    return transaction?.method?.name;
  }

  /**
   * Gets the status translation key based on transaction status
   * Maps status IDs to corresponding chip translation keys
   *
   * @returns {string} Translation key for the status chip
   *
   * @example
   * // For paid status
   * getStatus() // Returns "chip:chip_success"
   *
   * // For pending status
   * getStatus() // Returns "chip:chip_pending"
   */
  function getStatus() {
    switch (transaction?.status?.id?.toString()) {
      case DepositStatusId.Paid?.toString():
        return 'chip:chip_success';
      case DepositStatusId.Expired?.toString():
        return 'chip:chip_expired';
      case DepositStatusId.Pending?.toString():
      case DepositStatusId.Created?.toString():
        return 'chip:chip_pending';
      case DepositStatusId.Refunded?.toString():
        return 'chip:chip_refunded';
      default:
        return 'chip:chip_pending';
    }
  }

  if (isLoading) {
    return <TransactionResultDetailsSkeleton minimal={minimal} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__card}>
        <span
          className={`${styles.container__text} ${styles['container__text--secondary']}`}
        >
          {t('transaction_id')}
        </span>
        <span
          className={`${styles.container__text} ${styles['container__text--primary']}`}
        >
          #{transaction?.id?.toString()}
        </span>
      </div>

      {!minimal && (
        <div className={styles.container__card}>
          <span
            className={`${styles.container__text} ${styles['container__text--secondary']}`}
          >
            {t('common:created_at')}
          </span>

          <span
            className={`${styles.container__text} ${styles['container__text--primary']}`}
          >
            {new Intl.DateTimeFormat('pt-BR', {
              hour: 'numeric',
              minute: 'numeric',
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
            }).format(new Date(transaction?.createdAt))}
          </span>
        </div>
      )}
      {!minimal && (
        <div className={styles.container__card}>
          <span
            className={`${styles.container__text} ${styles['container__text--secondary']}`}
          >
            {modal === CommonModalsKey.Deposit
              ? t('deposit_method_label')
              : t('withdraw:withdraw_method_choice')}
          </span>
          <div
            className={`${styles.container__row} ${styles['container__row--small']}`}
          >
            <Image
              format="square"
              width={18}
              height={18}
              alt="icon-square"
              src={`${SettingsService.env.S3_BUCKET_BASE_URL}/${
                transaction?.method?.id?.toString() ===
                DepositMethodId.Crypto?.toString()
                  ? 'coins/rounded'
                  : 'icons'
              }/${
                transaction?.method?.id?.toString() ===
                DepositMethodId.Crypto?.toString()
                  ? transaction?.coin?.image
                  : transaction?.method?.image
              }`}
              secondarySkeleton
              className={styles.container__image}
            />

            <span
              className={`${styles.container__text} ${styles['container__text--primary']}`}
            >
              {t(getDepositMethodName())}
            </span>
          </div>
        </div>
      )}

      <div className={styles.container__card}>
        <span
          className={`${styles.container__text} ${styles['container__text--secondary']}`}
        >
          {t('status')}
        </span>
        <div
          className={`${styles.container__row} ${styles['container__row--small']}`}
        >
          <div
            className={`${styles.container__dot} ${styles[`container__dot--${transaction?.status?.id?.toString() ?? DepositStatusId.Pending?.toString()}`]}`}
          />
          <span
            className={`${styles.container__text} ${styles['container__text--primary']}`}
          >
            {t(getStatus())}
          </span>
        </div>
      </div>

      {transaction?.transaction?.url && (
        <div className={styles.container__card}>
          <span
            className={`${styles.container__text} ${styles['container__text--secondary']}`}
          >
            {t('transaction_url')}
          </span>
          <div
            className={`${styles.container__row} ${styles['container__row--small']}`}
          >
            <a
              href={transaction?.transaction?.url}
              target="_blank"
              className={`${styles.container__text} ${styles['container__text--link']}`}
              rel="noreferrer"
            >
              {transaction?.transaction?.url}
            </a>
          </div>
        </div>
      )}

      {transaction?.transaction?.transfers?.length > 0 && !minimal && (
        <div
          className={`${styles.container__card} ${styles['container__card--row']}`}
        >
          <span
            className={`${styles.container__text} ${styles['container__text--secondary']}`}
          >
            <ToolTip titleComponent={t('common:transactions_mapping_tooltip')}>
              <i className="fa-solid fa-circle-info" />
            </ToolTip>
            {'  '}
            {t('common:transactions_mapping_title')}
          </span>
          <div
            className={`${styles.container__row} ${styles['container__row--small']}`}
          >
            <TransactionsMapping />
          </div>
        </div>
      )}
    </div>
  );
}
